import { useQuery } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, ReactElement } from "react";
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import {
  ArchiveDataBrokersMutation,
  ArchiveDataBrokersMutationVariables,
  ListDataBrokersQuery,
  UnarchiveDataBrokersMutation,
  UnarchiveDataBrokersMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  ARCHIVE_DATA_BROKERS,
  LIST_DATA_BROKERS,
  UNARCHIVE_DATA_BROKERS,
} from "~frontend/graphql/dataBroker";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import useMutation from "~frontend/util/useMutation";

import DataBrokerDialog from "./DataBrokerDialog";
import DataBrokerTable, { DataBrokerTableRow } from "./DataBrokerTable";
import { DATA_BROKER_SORTS, DataBrokerSort } from "./constants";

function validateSort(sort: string): DataBrokerSort {
  return (
    DATA_BROKER_SORTS.find(s => s.key === sort)?.key ?? DATA_BROKER_SORTS[0].key
  );
}

export default function DataBrokers(): ReactElement {
  useBreadcrumbs([
    {
      label: "Data Brokers",
      path: "/staff/data-brokers",
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, DATA_BROKER_SORTS[0].key)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const validatedSort = validateSort(sort);

  const { loading, error, data } =
    useQuery<ListDataBrokersQuery>(LIST_DATA_BROKERS);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DataBrokerTableRow | null>(null);

  const [archiveDataBroker] = useMutation<
    ArchiveDataBrokersMutation,
    ArchiveDataBrokersMutationVariables
  >(ARCHIVE_DATA_BROKERS, {
    onErrorSnackbarMessage: "Error archiving data broker",
    refetchQueries: [LIST_DATA_BROKERS],
    awaitRefetchQueries: true,
  });

  const [unarchiveDataBroker] = useMutation<
    UnarchiveDataBrokersMutation,
    UnarchiveDataBrokersMutationVariables
  >(UNARCHIVE_DATA_BROKERS, {
    onErrorSnackbarMessage: "Error unarchiving data broker",
    refetchQueries: [LIST_DATA_BROKERS],
    awaitRefetchQueries: true,
  });

  const filteredDataBrokers: ListDataBrokersQuery["listDataBrokers"] =
    useMemo(() => {
      const allDataBrokers = data?.listDataBrokers ?? [];

      const filtered = search
        ? allDataBrokers.filter(
            d =>
              d.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
              d.slug.toLocaleLowerCase().includes(search.toLocaleLowerCase())
          )
        : allDataBrokers.slice(0);

      return filtered.sort((a, b) => {
        switch (validatedSort) {
          case "slug_asc":
            return (a.slug || "").localeCompare(b.slug || "");
          case "slug_desc":
            return (b.slug || "").localeCompare(a.slug || "");
          case "name_asc":
            return (a.name || "").localeCompare(b.name || "");
          case "name_desc":
            return (b.name || "").localeCompare(a.name || "");
          case "created_at_asc":
            return a.createdAt - b.createdAt;
          case "created_at_desc":
            return b.createdAt - a.createdAt;
        }
      });
    }, [data, validatedSort, search]);

  const rows = filteredDataBrokers.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  function handleAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function handleEdit(dataBroker: DataBrokerTableRow) {
    setEditing(dataBroker);
    setDialogOpen(true);
  }

  async function handleArchive(dataBrokerId: string) {
    const { data } = await archiveDataBroker({
      variables: { input: { dataBrokerIds: [dataBrokerId] } },
    });

    if (data?.archiveDataBrokers) {
      enqueueSnackbar(`Data broker archived`, { variant: "success" });
    } else {
      enqueueSnackbar(`Error archiving data broker`, { variant: "error" });
    }
  }

  async function handleUnarchive(dataBrokerId: string) {
    const { data } = await unarchiveDataBroker({
      variables: { input: { dataBrokerIds: [dataBrokerId] } },
    });

    if (data?.unarchiveDataBrokers) {
      enqueueSnackbar(`Data broker unarchived`, { variant: "success" });
    } else {
      enqueueSnackbar(`Error unarchiving data broker`, { variant: "error" });
    }
  }

  return (
    <PageContainer maxWidth="lg">
      <TableControls
        addButtons={[
          {
            label: "Add Data Broker",
            onClick: handleAdd,
            id: "add-data-broker",
          },
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as DataBrokerSort),
          options: DATA_BROKER_SORTS.map(s => ({
            value: s.key,
            label: s.label,
          })),
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search for a data broker...",
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <DataBrokerTable
            rows={rows}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={filteredDataBrokers.length}
            onEdit={handleEdit}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
          />
        )}
      </TableBox>
      {dialogOpen && (
        <DataBrokerDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          dataBroker={editing}
        />
      )}
    </PageContainer>
  );
}
